import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Building2, Zap, Sun, Leaf, FileCheck, Shield } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Requirement {
  title: string;
  content: string;
}

interface BuilderCitySectionProps {
  cityName: string;
  overview?: string;
  requirements: Requirement[];
  id?: string;
}

const getIconForRequirement = (title: string) => {
  const lowerTitle = title.toLowerCase();
  if (lowerTitle.includes('energy')) return Zap;
  if (lowerTitle.includes('ev')) return Zap;
  if (lowerTitle.includes('solar')) return Sun;
  if (lowerTitle.includes('green')) return Leaf;
  if (lowerTitle.includes('permit')) return FileCheck;
  if (lowerTitle.includes('historic') || lowerTitle.includes('flood')) return Shield;
  return FileCheck;
};

const getColorForRequirement = (title: string) => {
  const lowerTitle = title.toLowerCase();
  if (lowerTitle.includes('energy')) return 'text-amber-500 bg-amber-50 border-amber-200';
  if (lowerTitle.includes('ev')) return 'text-blue-500 bg-blue-50 border-blue-200';
  if (lowerTitle.includes('solar')) return 'text-orange-500 bg-orange-50 border-orange-200';
  if (lowerTitle.includes('green')) return 'text-emerald-500 bg-emerald-50 border-emerald-200';
  if (lowerTitle.includes('historic') || lowerTitle.includes('flood')) return 'text-purple-500 bg-purple-50 border-purple-200';
  return 'text-slate-500 bg-slate-50 border-slate-200';
};

const BuilderCitySection = ({ cityName, overview, requirements, id }: BuilderCitySectionProps) => {
  return (
    <div id={id} className="mb-8 scroll-mt-28">
      <h4 className="text-xl font-semibold text-fordje-navy mb-3 flex items-center gap-2">
        <Building2 className="h-5 w-5 text-fordje-blue" />
        {cityName}
      </h4>
      {overview && (
        <p className="text-muted-foreground mb-4 leading-relaxed">{overview}</p>
      )}
      
      <Accordion type="multiple" className="space-y-2">
        {requirements.map((req, index) => {
          const Icon = getIconForRequirement(req.title);
          const colors = getColorForRequirement(req.title);
          
          return (
            <AccordionItem
              key={index}
              value={`${cityName}-${req.title}`}
              className={cn('border rounded-lg overflow-hidden', colors.split(' ')[2])}
            >
              <AccordionTrigger className={cn('px-4 py-3 hover:no-underline', colors.split(' ')[1])}>
                <span className="flex items-center gap-3">
                  <span className={cn('p-1.5 rounded', colors)}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="font-medium text-foreground">{req.title}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 py-4 bg-white">
                <div 
                  className="prose prose-sm max-w-none [&>p]:mb-3 [&>p]:leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: req.content }}
                />
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
};

export default BuilderCitySection;
